<template>
	<div id="app" ref="app">
		<title-bar v-if="$route.meta.showNavbar"></title-bar>
		<loading v-if="$route.meta.showNavbar && isLoading" />
		<router-view />
		<system-lock />
	</div>
</template>

<script>
import TitleBar from "./components/common/TitleBar.vue";
import Loading from "./components/common/Loading.vue";
import SystemLock from "./components/common/SystemLock.vue";
import { checkVersionUpdate, reloadApp } from "@/utils/versionCheck";
export default {
	name: "App",
	components: {
		TitleBar,
		Loading,
		SystemLock
	},
	created() {},
	computed: {
		isLoading() {
			return this.$permissionStore.state.isLoading;
		}
	},
	mounted() {
		// 开发环境直接跳过版本检查
		if (process.env.NODE_ENV === "development") return;

		// 第一次检查
		this.checkUpdate();

		// 每 5 分钟检查一次
		this.versionTimer = setInterval(() => {
			this.checkUpdate();
		}, 5 * 60 * 1000);
	},
	methods: {
		async checkUpdate() {
			try {
				const needUpdate = await checkVersionUpdate();
				if (needUpdate) {
					await this.$alert("系统已更新，请刷新后使用", "更新提示", {
						confirmButtonText: "立即刷新",
						callback: (action) => {
							reloadApp();
						}
					});
				}
			} catch (err) {
				console.log("版本检查失败:", err);
			}
		}
	},
	beforeDestroy() {
		// 只有生产环境才需要清除定时器
		if (this.versionTimer) {
			clearInterval(this.versionTimer);
		}
	}
};
</script>

<style>
@import "assets/scss/style.scss";

/*
 * 注意：设计 token（--app-*）与 Element UI 主题覆盖已统一迁至
 * assets/scss/theme.scss，并在 main.js 中优先引入（早于 element-ui 的
 * theme-chalk，从而能覆盖它）。此处不再重复声明 :root 变量。
 */

html,
body {
	width: 100%;
	/* height: 100%; */
	box-sizing: border-box;
	padding: 0px;
	margin: 0px;
	background-color: var(--app-page-bg) !important;
	letter-spacing: 1px;
	padding-right: 0 !important;
}

#app {
	font-family: "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	color: var(--app-text);
	width: 100%;
	height: 100%;
	overflow: hidden;
}

section {
	height: 100%;
}

.el-input.is-disabled .el-input__inner {
	color: var(--app-text-secondary);
	/* font-style: oblique; */
}

.el-dialog {
	background-color: var(--app-surface) !important;
	border: 1px solid var(--app-border);
	border-radius: 12px;
	overflow: hidden;
}

.el-dialog__header {
	background: var(--app-surface-muted);
	padding: 14px 18px;
	border-bottom: 1px solid var(--app-border);
}

.user-search {
	background: var(--app-surface);
	margin: 0 0 10px 0;
	padding: 14px 16px 6px;
}

/* float:right 按钮组始终定位到最后一列右对齐 */
.user-search .el-form-item[style*="float: right"] {
	grid-column-end: -1;
	justify-self: end;
}

/* 防止表单项内容溢出 */
.user-search .el-form-item {
	min-width: 0;
}

.el-dialog__title {
	font-size: 18px;
	font-weight: 600;
	color: var(--app-text);
}

.el-dialog__close {
	background-color: transparent;
}

.el-dialog__headerbtn .el-dialog__close {
	color: var(--app-text-muted);
}

.el-dialog__headerbtn:hover .el-dialog__close {
	color: var(--app-text);
}

.el-message--info {
	background: var(--app-surface-muted);
}

.el-message--info .el-message__content {
	color: var(--app-text);
}

.el-button > span {
	margin-left: 5px;
}

.el-dialog__headerbtn {
	top: 14px;
	font-size: 18px;
}

.el-dialog__body {
	background-color: var(--app-surface);
	padding: 18px !important;
}

.el-dialog__footer {
	background-color: var(--app-surface);
	padding: 14px 18px 18px;
}

.el-input__inner {
	border: 1px solid var(--app-border-strong);
	border-radius: var(--app-radius);
}

.el-input__inner:hover {
	border: 1px solid var(--app-primary-border);
}

.el-select .el-input__inner,
.el-date-editor .el-input__inner,
.el-range-editor.el-input__inner {
	border-radius: var(--app-radius);
}

.el-card {
	border: 1px solid var(--app-border);
	border-radius: var(--app-radius-lg);
	box-shadow: var(--app-shadow);
	overflow: hidden;
}

.el-card__header {
	padding: 14px 16px;
	background: var(--app-surface-muted);
	border-bottom: 1px solid var(--app-border);
	font-weight: 600;
	color: var(--app-text);
}

.el-card__body {
	padding: 16px;
}

.el-button {
	border-radius: var(--app-radius);
	font-weight: 500;
}

.el-tag {
	border-radius: 999px;
	padding: 0 10px;
	border: none;
	white-space: nowrap;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	height: 1.5rem !important;
	line-height: 1.5rem !important;
}

.el-tabs__nav-wrap::after {
	height: 1px;
	background-color: var(--app-border);
}

.el-tabs__item {
	color: var(--app-text-secondary);
}

.el-tabs__item.is-active {
	color: var(--app-primary-text);
	font-weight: 600;
}

.el-pagination {
	padding: 0;
}

.el-empty__description {
	color: var(--app-text-secondary);
}

.el-breadcrumb__inner,
.el-breadcrumb__inner a {
	color: var(--app-text-secondary);
}

.el-form-item {
	margin-bottom: 8px;
	margin-top: 8px;
}

.el-form-item__error {
	padding-top: 0px;
}

.el-tooltip__popper {
	max-width: 800px;
}

.el-button--mini {
	padding: 7px 10px;
}

.pd20 {
	padding: 0 16px 16px;
	height: calc(100vh - 130px);
	background: transparent;
	overflow: auto;
}

.pagination {
	display: flex;
	justify-content: flex-end;
	padding: 12px 0 0;
}

.drawer-footer {
	position: fixed;
	bottom: 0px;
	right: 0px;
	width: 600px;
	height: 50px;
	background: var(--app-surface-muted);
	display: flex;
	align-items: center;
	border-top: 1px solid var(--app-border);
	padding-left: 20px;
}

.el-drawer__header {
	margin-bottom: 15px;
	padding: 15px 15px;
	border-bottom: 1px solid var(--app-border-light);
}

/* 弹出框分割线 --目前只包含底部线 */
.dialog-footer {
	display: flex;
	justify-content: flex-end;
	gap: 12px;
}

.fontWeight600 {
	font-weight: 600;
}

.router-tab__item {
	width: auto !important;
	min-width: 0 !important;
	max-width: none !important;
	padding: 0 12px !important;
	justify-content: center;
	color: var(--app-text-secondary) !important;
	font-size: 13px !important;
	height: 34px;
	box-sizing: border-box;
	border: 1px solid transparent !important;
	border-radius: 8px;
	background: transparent;
	transition: color .18s, background-color .18s, border-color .18s, box-shadow .18s;
}

.router-tab__item.is-active {
	background: var(--app-primary-soft);
	color: var(--app-primary-text) !important;
	border-color: var(--app-primary-border) !important;
	font-weight: 600;
	box-shadow: inset 0 -2px 0 var(--app-primary);
}

.router-tab__header { background: var(--app-surface); border-bottom: 1px solid var(--app-border); padding: 0 10px; }
.router-tab__nav { align-items: center; gap: 6px; }
.router-tab__item:hover { background: var(--app-surface-hover); color: var(--app-primary-text) !important; }
.router-tab__item.is-active:hover { background: var(--app-primary-soft-hover); }
.router-tab__item-icon { font-size: 15px; margin-right: 7px; }
.router-tab__item .router-tab__item-close,
.router-tab__item.is-active .router-tab__item-close,
.router-tab__item:hover .router-tab__item-close { width: 18px; height: 18px; margin-left: 8px; border-radius: 5px; opacity: .55; }
.router-tab__item .router-tab__item-close::before,
.router-tab__item .router-tab__item-close::after { top: 8px; background: currentColor; }
.router-tab__item:hover .router-tab__item-close,
.router-tab__item.is-active .router-tab__item-close { opacity: 1; }
.router-tab__item .router-tab__item-close:hover { background: var(--app-primary); color: var(--app-primary-contrast); }
.router-tab__item .router-tab__item-close:hover::before,
.router-tab__item .router-tab__item-close:hover::after { background: currentColor; }
.router-tab__scrollbar { background: var(--app-surface-sunken); }
.router-tab__scrollbar-thumb { background: var(--app-border-strong); }
.router-tab__scrollbar-thumb:hover,
.router-tab__scrollbar.is-dragging .router-tab__scrollbar-thumb { background: var(--app-primary); }
.router-tab__contextmenu { background: var(--app-surface-raised); border-color: var(--app-border); border-radius: 8px; box-shadow: var(--app-shadow); }
.router-tab__contextmenu-item { color: var(--app-text-secondary); }
.router-tab__contextmenu-item:hover { background: var(--app-primary-soft); color: var(--app-primary-text); }
.router-tab__contextmenu-item[disabled] { color: var(--app-text-placeholder); }

.router-tab__container {
	background: var(--app-page-bg) !important;
}

/* 标题宽度自适应文字长度，不固定、不省略 */
.router-tab__item-title {
	min-width: 0 !important;
	max-width: none !important;
	overflow: visible !important;
	text-overflow: clip !important;
	white-space: nowrap;
}

.router-tab {
	min-height: 0;
}

/* 列表主体的高度 */
.table-minheight {
	min-height: calc(100vh - 258px);
	background: var(--app-surface) !important;
	border-radius: 12px;
	border: 1px solid var(--app-border);
	box-shadow: var(--app-shadow);
}

.radius-10 {
	border-radius: 12px;
}

.usercym {
	height: 100%;
	min-height: 0;
	min-width: 0;
	display: flex;
	gap: 16px;
	border-radius: 12px;
}

.user_right {
	flex: 1;
	min-width: 0;
	min-height: 0;
	height: 100%;
	max-height: 100%;
	overflow: auto;
	padding-left: 0;
}

.pic_bg {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
}

.pic {
	width: 36px;
	height: 36px;
	object-fit: cover;
}

.imgBig {
	width: 100%;
	max-height: 80vh;
	object-fit: contain;
}

/* 性别标签 .one_1 / .one_2 的唯一定义已迁至 assets/scss/theme.scss
   （男=主色 / 女=成功色），此处不再重复声明。 */

.down_btn {
	color: var(--app-primary-text);
	cursor: pointer;
	font-weight: 600;
}

.table_div {
	width: 100%;
	min-height: 20px;
	line-height: 20px;
	display: table;
	color: var(--app-text-secondary);
}

.redTip {
	color: var(--app-danger-text);
	font-size: 12px;
	padding: 12px 12px;
	background: var(--app-danger-soft);
	border: 1px solid var(--app-danger-border);
	border-radius: 10px;
	margin: 12px 0;
}

.fontWight600,
.fontWeight600 {
	font-weight: 600;
	font-size: 14px;
	padding-bottom: 3px;
	color: var(--app-text);
}

.colorG {
	width: 100%;
	height: 14px;
	border-radius: 999px;
}

/* 面包屑默认样式 */
.el-breadcrumb__inner {
	color: var(--app-text-muted);
}

.el-form--label-top .el-form-item__label {
	padding: 0px 0px;
}

.el-popover {
	max-width: 400px;
	/* 限制宽度 */
	max-height: 400px;
	/* 限制高度 */
	overflow: auto;
	/* 当内容超出时显示滚动条 */
	margin-left: 10px;
}

.el-input-number .el-input__inner {
	text-align: left;
}

.el-message {
	z-index: 99999999 !important;
}

.add_bg {
	width: 800px;
	margin: 0 auto;
}

.input_200px {
	width: 400px !important;
}

.input_dialog {
	width: 1000px !important;
}

.select_200px {
	width: 200px !important;
}

.margin_top_40 {
	margin-top: 3.5rem;
}

.margin_bottom_40 {
	margin-bottom: 0px;
}

.add_bg_800px {
	width: 800px;
	margin: 0 auto;
}

.state_box {
	background: var(--app-success);
	color: var(--app-text-inverse);
	width: 70px;
	height: 24px;
	border-radius: var(--app-radius-sm);
	padding: 0 8px;
	box-sizing: border-box;
	display: inline-block;
	line-height: 24px;
	text-align-last: justify;
}

.separate_div {
	width: 100%;
	height: 10px;
	background: var(--app-page-bg);
}

.line_div {
	width: 100%;
	height: 1px;
	background: var(--app-page-bg);
}

.el-drawer {
	width: 600px !important;
}

.page-section-title {
	padding: 14px 16px 8px;
	font-size: 16px;
	font-weight: 600;
	color: var(--app-text);
}

.page-subtitle {
	padding: 0 16px 10px;
	font-size: 12px;
	color: var(--app-text-muted);
}

.el-table {
	border-radius: 12px;
	overflow: hidden;
}

.el-table thead th {
	background: var(--app-table-header-bg) !important;
	color: var(--app-text);
	font-weight: 600;
	height: 48px;
}

.el-table td,
.el-table th.is-leaf {
	border-bottom-color: var(--app-table-border);
}
</style>
